import axios, { AxiosError, AxiosHeaders, CanceledError } from "axios";
import { Capacitor, CapacitorCookies, CapacitorHttp } from "@capacitor/core";
import { App } from "@capacitor/app";

export function assertNativeApiUrl(value) {
  let url;
  try {
    url = new URL(value);
  } catch {
    throw new Error("Native apps require an absolute HTTPS API URL.");
  }
  if (url.protocol !== "https:" || url.username || url.password || url.search || url.hash) {
    throw new Error("Native apps require an absolute HTTPS API URL without credentials, query or fragment.");
  }
  return url.href.replace(/\/+$/, "");
}

export function nativeOrigin() {
  const platform = Capacitor.getPlatform();
  if (platform === "ios") return "capacitor://localhost";
  if (platform === "android") return "https://localhost";
  throw new Error("Unsupported native app origin.");
}

// The shared web build keeps its existing cookie-authentication contract.
// CapacitorHttp must be enabled in the native config (checked at native build).
// It routes Axios's XHR through the native cookie jar without copying tokens
// into JavaScript storage. Headers are limited to this build's API only.
export function configureNativeRuntime({
  backendUrl = process.env.REACT_APP_BACKEND_URL,
  client = axios,
  origin = `${window.location.protocol}//${window.location.host}`,
} = {}) {
  if (!Capacitor.isNativePlatform()) return () => {};
  const apiBase = assertNativeApiUrl(backendUrl);
  const expectedOrigin = nativeOrigin();
  if (origin !== expectedOrigin) {
    throw new Error(`Native app origin must be ${expectedOrigin}.`);
  }
  const interceptor = client.interceptors.request.use((config) => {
    let url;
    try {
      url = new URL(client.getUri(config), origin);
    } catch {
      return config;
    }
    if (isApiUrl(url, apiBase)) {
      config.headers = AxiosHeaders.from(config.headers);
      config.headers.set("X-Client-Platform", "mobile");
      config.headers.set("Origin", expectedOrigin);
      config.withCredentials = true;
      if (config.responseType === "blob") {
        config.adapter = nativeBinaryAdapter(client, apiBase);
      }
    }
    return config;
  });
  return () => client.interceptors.request.eject(interceptor);
}

// Capacitor 8's patched mutation XHR does not forward responseType. Explicit
// binary requests avoid decoding POST ZIP responses as UTF-8 text. Ordinary
// JSON and multipart requests continue through Capacitor's patched XHR.
function nativeBinaryAdapter(client, apiBase) {
  return async (config) => {
    const checkCancelled = () => {
      config.cancelToken?.throwIfRequested();
      if (config.signal?.aborted) throw new CanceledError("Download cancelled", config);
    };
    checkCancelled();
    if (config.data instanceof FormData || config.data instanceof Blob || config.data instanceof ArrayBuffer) {
      throw new AxiosError("Native binary downloads require a JSON request body.", AxiosError.ERR_NOT_SUPPORT, config);
    }
    const options = {
      url: client.getUri(config), method: (config.method || "get").toUpperCase(),
      headers: AxiosHeaders.from(config.headers).toJSON(), data: config.data,
      responseType: "arraybuffer", disableRedirects: true,
      ...(config.timeout > 0 ? { readTimeout: config.timeout, connectTimeout: config.timeout } : {}),
    };
    const native = await followNativeDownload(options, apiBase, checkCancelled);
    checkCancelled();
    const headers = AxiosHeaders.from(native.headers);
    const data = nativeResponseBlob(native);
    const response = { data, status: native.status, statusText: "", headers, config, request: null };
    if (config.validateStatus && !config.validateStatus(native.status)) {
      throw new AxiosError(`Request failed with status code ${native.status}`,
        native.status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
        config, null, response);
    }
    return response;
  };
}

function nativeResponseBlob(response) {
  const type = AxiosHeaders.from(response.headers).get("content-type") || "application/octet-stream";
  if (/\bjson\b/i.test(type)) return new Blob([JSON.stringify(response.data)], { type });
  if (response.status >= 400 && Capacitor.getPlatform() === "android") {
    // Android's HTTP errorStream is returned as text despite arraybuffer.
    return new Blob([response.data ?? ""], { type });
  }
  if (typeof response.data !== "string") throw new Error("Native download did not return binary data.");
  const decoded = window.atob(response.data);
  const bytes = Uint8Array.from(decoded, (character) => character.charCodeAt(0));
  return new Blob([bytes], { type });
}

export async function fetchNativeAttachmentBlob(value, backendUrl = process.env.REACT_APP_BACKEND_URL) {
  let target;
  try { target = new URL(value); } catch {
    throw new Error("External attachments require an absolute HTTPS URL without credentials.");
  }
  if (target.protocol !== "https:" || target.username || target.password) {
    throw new Error("External attachments require an absolute HTTPS URL without credentials.");
  }
  const apiOrigin = new URL(assertNativeApiUrl(backendUrl)).origin;
  if (target.origin === apiOrigin) throw new Error("External attachments must use a different origin from the API.");
  // Never copy Axios defaults, auth headers, Origin or cookie values to signed
  // storage. CapacitorHttp has no cookie-omit option: its native cookie jar
  // still applies per-domain rules, so the API must keep host-only cookies.
  const response = await CapacitorHttp.request({
    url: target.href, method: "GET", headers: {}, responseType: "arraybuffer", disableRedirects: true,
  });
  if ((response.status >= 300 && response.status < 400) || (response.url && response.url !== target.href)) {
    throw new Error("External attachment redirects are not allowed.");
  }
  if (response.status !== 200) throw new Error(`Attachment download failed with status code ${response.status}.`);
  return nativeResponseBlob(response);
}

async function followNativeDownload(initial, apiBase, checkCancelled) {
  let options = initial;
  for (let redirects = 0; redirects <= 5; redirects += 1) {
    checkCancelled();
    const response = await CapacitorHttp.request(options);
    checkCancelled();
    if (![301, 302, 303, 307, 308].includes(response.status)) return response;
    const location = AxiosHeaders.from(response.headers).get("location");
    if (!location) return response;
    if (redirects === 5) throw new Error("Too many file download redirects.");
    const target = new URL(location, response.url || options.url);
    if (target.protocol !== "https:" || target.username || target.password) {
      throw new Error("File downloads require an HTTPS redirect without credentials.");
    }
    if (!["GET", "HEAD"].includes(options.method) && [307, 308].includes(response.status)) {
      throw new Error("A file download cannot redirect a request body.");
    }
    // Protected endpoints issue short-lived signed-storage redirects. Keep
    // their cookie-authenticated lookup inside the app, then drop every API
    // header before fetching the signed HTTPS destination. Native cookie jars
    // continue applying their own per-domain rules; no cookie values enter JS.
    options = {
      ...initial, url: target.href, method: "GET", data: undefined,
      headers: isApiUrl(target, apiBase) ? initial.headers : {},
    };
  }
  throw new Error("File download could not be completed.");
}

export function isApiUrl(url, backendUrl = process.env.REACT_APP_BACKEND_URL) {
  const base = new URL(backendUrl || "/", window.location.href);
  const root = `${base.pathname.replace(/\/+$/, "")}/api`;
  return url.origin === base.origin && !url.username && !url.password &&
    (url.pathname === root || url.pathname.startsWith(`${root}/`));
}

// The existing mobile entitlement header also asks the server for body tokens.
// The native HTTP cookie jar owns the session; UI state needs only user data.
export function stripSessionTokens(session) {
  if (!session) return session;
  const user = { ...session };
  delete user.access_token;
  delete user.refresh_token;
  return user;
}

export async function clearNativeSessionCookies(backendUrl = process.env.REACT_APP_BACKEND_URL) {
  if (!Capacitor.isNativePlatform()) return;
  const url = new URL(assertNativeApiUrl(backendUrl));
  await CapacitorCookies.clearCookies({ url: url.origin });
}

// Return cleanup immediately: React may unmount before native registration
// resolves. Removing our own handle avoids affecting another component.
export function addNativeAppListener(eventName, listener, onError = () => {}) {
  if (!Capacitor.isNativePlatform()) return () => {};
  let disposed = false;
  let handle;
  const remove = (value) => Promise.resolve(value.remove()).catch(onError);
  Promise.resolve(App.addListener(eventName, (event) => {
    if (!disposed) listener(event);
  })).then((registered) => {
    if (disposed) remove(registered);
    else handle = registered;
  }).catch(onError);
  return () => {
    if (disposed) return;
    disposed = true;
    if (handle) remove(handle);
  };
}
